import { FC, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Checkbox, Divider, Dropdown, Menu } from 'antd';
import { capitalCase } from 'change-case';
import { TbAdjustmentsHorizontal, TbArrowUpRight, TbBuildingBank, TbCertificate, TbUser } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
} from '@/shared/components/dropdowns';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DashedPane } from '@/shared/components/layouts/sections';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { VirtualizedList as List, SkeletonList } from '@/shared/components/lists';
import { BaseSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import { corporateLogo, dayjs, ellipsisStyling } from '@/utilities';

import { useFinancialOverviewViewPreference } from '../../hooks/useFinancialOverviewViewPreference';
import { FinancialOverviewInstitution, FinancialOverviewInstitutionalHoldingsProps } from '../../types';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';
import {
    FinancialOverviewIndividualHolding,
    individualHoldingsQueryOptions,
    institutionalHoldingsQueryOptions,
} from './data';
import { resolveInstitutionWebsite } from './institutionWebsites';

type HoldingListItem =
    | { holding: FinancialOverviewIndividualHolding; key: string; kind: 'individual'; shares: number }
    | { holding: FinancialOverviewInstitution; key: string; kind: 'institution'; shares: number };

const getInstitutionKey = (institution: FinancialOverviewInstitution, index: number) =>
    institution.investorName ?? institution.holder ?? `institution-${index}`;

export const FinancialOverviewInstitutionalHoldings: FC<FinancialOverviewInstitutionalHoldingsProps> = ({
    cik,
    entityName,
    preferenceKey = 'default',
    tickerSymbol,
}) => {
    const [displayCount, setDisplayCount] = useFinancialOverviewViewPreference(
        preferenceKey,
        'institutionalDisplayCount',
        10,
        (value): value is number => typeof value === 'number' && [5, 10, 25, 50].includes(value),
    );
    const [includeIndividualHoldings, setIncludeIndividualHoldings] = useFinancialOverviewViewPreference(
        preferenceKey,
        'includeIndividualHoldings',
        false,
        (value): value is boolean => typeof value === 'boolean',
    );
    const institutionalQuery = useQuery({
        ...institutionalHoldingsQueryOptions(tickerSymbol ?? ''),
        enabled: Boolean(tickerSymbol),
    });
    const individualQuery = useQuery({
        ...individualHoldingsQueryOptions(tickerSymbol ?? ''),
        enabled: includeIndividualHoldings && Boolean(tickerSymbol),
    });
    const loading =
        institutionalQuery.isLoading ||
        (includeIndividualHoldings && individualQuery.isLoading && (institutionalQuery.data?.length ?? 0) === 0);
    const holdings = useMemo<HoldingListItem[]>(() => {
        const institutions = institutionalQuery.data ?? [];
        const individualHoldings = individualQuery.data ?? [];
        const institutionalRows: HoldingListItem[] = institutions.slice(0, displayCount).map((holding, index) => ({
            holding,
            key: `institution-${getInstitutionKey(holding, index)}`,
            kind: 'institution',
            shares: holding.lastSharesNumber ?? 0,
        }));
        const individualRows: HoldingListItem[] = includeIndividualHoldings
            ? individualHoldings.slice(0, displayCount).map((holding, index) => ({
                  holding,
                  key: `individual-${holding.reportingName ?? index}`,
                  kind: 'individual',
                  shares: holding.securitiesOwned ?? 0,
              }))
            : [];

        return [...institutionalRows, ...individualRows].sort((left, right) => right.shares - left.shares);
    }, [displayCount, includeIndividualHoldings, individualQuery.data, institutionalQuery.data]);
    const holdingsControl = (
        <Dropdown
            popupRender={
                /* viewport-layout-allow: the holdings menu is a floating overlay constrained to the browser edge. */
                () => (
                    <DropdownPanel maxHeight='min(28rem, 70vh)' minWidth='20rem'>
                        <DropdownMenuLabel>Number of Holdings</DropdownMenuLabel>
                        <DropdownPanelMenu selectable selectedKeys={[`count-${displayCount}`]}>
                            {[5, 10, 25, 50].map((count) => (
                                <Menu.Item
                                    key={`count-${count}`}
                                    onClick={({ domEvent }) => {
                                        domEvent.stopPropagation();
                                        setDisplayCount(count);
                                    }}
                                >
                                    {count.toLocaleString('en-US')} Holdings
                                </Menu.Item>
                            ))}
                        </DropdownPanelMenu>
                        <Divider style={{ margin: 0 }} />
                        <DropdownPanelMenu selectable={false}>
                            <Menu.Item
                                key='individual-holdings'
                                onClick={({ domEvent }) => {
                                    domEvent.stopPropagation();
                                    setIncludeIndividualHoldings(!includeIndividualHoldings);
                                }}
                            >
                                <DropdownOptionContent
                                    control={<Checkbox checked={includeIndividualHoldings} />}
                                    description='Include reported people and insider positions alongside institutions.'
                                    selected={includeIndividualHoldings}
                                    title='People Holdings'
                                />
                            </Menu.Item>
                        </DropdownPanelMenu>
                    </DropdownPanel>
                )
            }
            placement='bottomRight'
            trigger={['click']}
        >
            <DropdownButton icon={<TbAdjustmentsHorizontal />} size='small'>
                {displayCount.toLocaleString('en-US')} Institutions
            </DropdownButton>
        </Dropdown>
    );

    return (
        <FinancialOverviewPanel
            actions={
                tickerSymbol ? (
                    loading ? (
                        <ButtonSkeleton size='small' style={{ width: '8rem' }} />
                    ) : (
                        holdingsControl
                    )
                ) : undefined
            }
            description={`Review the largest reported institutional${includeIndividualHoldings ? ' and people' : ''} positions and filing dates.`}
            icon={<TbBuildingBank />}
            title='Top Institutional Holdings'
        >
            {holdings.length > 0 && !loading ? (
                <DashedPane height='20rem' marginTop='0.5rem'>
                    <List
                        dataSource={holdings}
                        getItemKey={(item) => item.key}
                        loading={loading}
                        renderItem={(item: HoldingListItem) =>
                            item.kind === 'institution' ? (
                                <FinancialOverviewInstitutionalHoldingListItem cik={cik} institution={item.holding} />
                            ) : (
                                <FinancialOverviewIndividualHoldingListItem holding={item.holding} />
                            )
                        }
                        size='small'
                    />
                </DashedPane>
            ) : loading ? (
                <>
                    <BaseSkeleton paragraph={{ rows: 0 }} style={{ marginBottom: '-1rem' }} />
                    <SkeletonList listLength={6} minHeight='22rem' maxHeight='22rem' />
                </>
            ) : (
                <EmptySection
                    description={`No reported holdings are currently available for ${entityName ?? 'this company'}.`}
                    height='20rem'
                    icon={<TbBuildingBank size='1.25rem' />}
                    label='Holdings Unavailable'
                />
            )}
        </FinancialOverviewPanel>
    );
};

const FinancialOverviewInstitutionalHoldingListItem: FC<{
    cik?: string;
    institution: FinancialOverviewInstitution;
}> = ({ cik, institution }) => {
    const { token } = useAntDesignTheme();
    const website = resolveInstitutionWebsite(institution);
    const institutionName = institution.investorName ?? institution.holder;
    const secHref = `https://www.sec.gov/edgar/search/?r=el#/filter_entityName=${encodeURIComponent(
        institutionName ?? '',
    )}&dateRange=all${cik ? `&ciks=${encodeURIComponent(cik)}` : ''}`;

    return (
        <List.Item style={{ margin: 0, padding: '0.5rem 0' }}>
            <List.Item.Meta
                avatar={
                    <Avatar
                        alt={institutionName}
                        icon={<TbBuildingBank />}
                        shape='square'
                        size={28}
                        src={website ? corporateLogo(website) : undefined}
                        style={{
                            backgroundColor: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                        }}
                    />
                }
                description={
                    <small>
                        {institution.filingDate ? `As of ${dayjs(institution.filingDate).format('LL')}` : '-'}
                    </small>
                }
                title={
                    <span style={{ fontWeight: 500 }}>
                        {institutionName ? (
                            <a
                                href={secHref}
                                target='_blank'
                                rel='noopener noreferrer'
                                style={{
                                    ...ellipsisStyling,
                                    color: 'inherit',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                }}
                            >
                                {capitalCase(institutionName)}
                                <TbArrowUpRight style={{ marginLeft: '0.25rem', verticalAlign: '-0.15rem' }} />
                            </a>
                        ) : (
                            '-'
                        )}
                    </span>
                }
            />
            <small>
                <TbCertificate
                    style={{ color: token.colorTextTertiary, marginRight: '0.25rem', verticalAlign: '-0.1rem' }}
                />
                {institution.lastSharesNumber === undefined
                    ? '-'
                    : institution.lastSharesNumber.toLocaleString('en-US')}
            </small>
        </List.Item>
    );
};

const FinancialOverviewIndividualHoldingListItem: FC<{
    holding: FinancialOverviewIndividualHolding;
}> = ({ holding }) => {
    const { token } = useAntDesignTheme();
    const name = holding.reportingName ?? 'Unnamed individual';
    const title = <span style={{ fontWeight: 500 }}>{capitalCase(name)}</span>;

    return (
        <List.Item style={{ margin: 0, padding: '0.5rem 0' }}>
            <List.Item.Meta
                avatar={
                    <Avatar
                        alt={name}
                        icon={<TbUser />}
                        shape='square'
                        size={28}
                        style={{
                            backgroundColor: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                        }}
                    />
                }
                description={
                    <small>
                        {[
                            holding.typeOfOwner,
                            holding.filingDate ? `As of ${dayjs(holding.filingDate).format('LL')}` : null,
                        ]
                            .filter(Boolean)
                            .join(' · ') || 'Reported individual holding'}
                    </small>
                }
                title={
                    holding.link ? (
                        <a href={holding.link} target='_blank' rel='noopener noreferrer' style={{ color: 'inherit' }}>
                            {title}
                        </a>
                    ) : (
                        title
                    )
                }
            />
            <small>
                <TbCertificate
                    style={{ color: token.colorTextTertiary, marginRight: '0.25rem', verticalAlign: '-0.1rem' }}
                />
                {holding.securitiesOwned === undefined ? '-' : holding.securitiesOwned.toLocaleString('en-US')}
            </small>
        </List.Item>
    );
};
